import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { getAuthErrorCode, getAuthUserMessage, shouldLogOutForAuthError } from "../auth/authTypes";
import { API_VERSION } from "./sessionRoutes";
import { clearStoredToken, getIdToken } from "./token";

const SESSION_STORAGE_KEY = "session_id";
const isBrowser = typeof window !== "undefined";

const API_BASE_URL =
  process.env.NODE_ENV === "development" ? "" : (process.env.REACT_APP_BASE_URL ?? "");

let inMemorySessionId: string | null = null;
let sessionCreationPromise: Promise<string | null> | null = null; // Shared in-flight creation request
let sessionCreationVersion = 0; // Monotonic guard against stale promise resolution

export class SessionLimitError extends Error {
  constructor(public readonly payload: unknown) {
    super("Session creation blocked by concurrent session limit");
    this.name = "SessionLimitError";
    Object.setPrototypeOf(this, SessionLimitError.prototype);
  }
}

const sessionClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "X-API-Version": API_VERSION,
  },
  withCredentials: true,
});

const invalidateSessionCreation = () => {
  sessionCreationVersion += 1;
  sessionCreationPromise = null; // Drop references so future calls start fresh
};

const setSessionId = (sessionId: string | null) => {
  inMemorySessionId = sessionId;
  if (!isBrowser) return;

  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

export const getSessionId = (): string | null => {
  if (inMemorySessionId) {
    return inMemorySessionId;
  }

  if (isBrowser) {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
  return null;
};

export const clearSession = () => {
  invalidateSessionCreation();
  setSessionId(null);
};

const handleSessionError = (error: unknown) => {
  if (!axios.isAxiosError(error)) return;
  const data = error.response?.data;
  if (shouldLogOutForAuthError(data)) {
    clearSession();
    clearStoredToken();
  }
};

const isMaxConcurrentSessionLimit = (payload: unknown): boolean => {
  const code = getAuthErrorCode(payload);
  if (code === "max_concurrent_sessions_exceeded") {
    return true;
  }

  const message = getAuthUserMessage(payload)?.toLowerCase() ?? "";
  return message.includes("maximum concurrent sessions exceeded");
};

const extractSessionId = (payload: unknown): string | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const seen = new WeakSet<object>();
  const stack: unknown[] = [payload];

  const readSessionKeys = (
    value: Record<string, unknown>,
    includeGenericId = false,
  ): string | null => {
    const sessionIdCandidates = [value.session_id, value.sessionId];
    if (includeGenericId) {
      sessionIdCandidates.push(value.id);
    }

    for (const candidate of sessionIdCandidates) {
      if (typeof candidate === "string" && candidate.length > 0) {
        return candidate;
      }
    }

    return null;
  };

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current || typeof current !== "object") {
      continue;
    }

    const objectValue = current as Record<string, unknown>;
    if (seen.has(objectValue)) {
      continue;
    }
    seen.add(objectValue);

    const direct = readSessionKeys(objectValue);
    if (direct) {
      return direct;
    }

    const sessionValue = objectValue.session;
    const sessionIdFromSessionObject =
      sessionValue && typeof sessionValue === "object"
        ? readSessionKeys(sessionValue as Record<string, unknown>, true)
        : null;
    if (sessionIdFromSessionObject) {
      return sessionIdFromSessionObject;
    }

    for (const value of Object.values(objectValue)) {
      if (!value || typeof value !== "object") {
        continue;
      }

      stack.push(value);
    }
  }

  return null;
};

const performSessionCreation = async (providedToken?: string): Promise<string | null> => {
  const token = providedToken || (await getIdToken());
  if (!token) {
    throw new Error("Unable to retrieve Firebase ID token");
  }

  const idempotencyKey = uuidv4();

  try {
    const response = await sessionClient.post(
      "/auth/session",
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
          "X-API-Version": API_VERSION,
        },
      },
    );

    if (process.env.NODE_ENV === "development") {
      console.log("POST /auth/session response", response.data);
    }

    const sessionId = extractSessionId(response.data);
    if (!sessionId) {
      console.warn("Session ID not present in /auth/session response; relying on cookie only", {
        data: response.data,
      });
      return null;
    }

    return sessionId;
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 429 &&
      isMaxConcurrentSessionLimit(error.response.data)
    ) {
      console.warn("Session creation blocked due to active session limit", {
        status: error.response.status,
        data: error.response.data,
      });
      throw new SessionLimitError(error.response?.data);
    }

    if (axios.isAxiosError(error)) {
      console.error("Session creation failed", {
        status: error.response?.status,
        data: error.response?.data,
      });
    } else {
      console.error("Session creation failed", error);
    }
    handleSessionError(error);
    throw error;
  }
};

export const createSession = async (providedToken?: string): Promise<string | null> => {
  if (sessionCreationPromise) {
    return sessionCreationPromise;
  }

  const attemptVersion = ++sessionCreationVersion;

  sessionCreationPromise = (async () => {
    try {
      const sessionId = await performSessionCreation(providedToken);
      if (sessionCreationVersion !== attemptVersion) {
        return null;
      }

      setSessionId(sessionId);
      return sessionId;
    } catch (error) {
      if (sessionCreationVersion === attemptVersion) {
        setSessionId(null);
      }
      throw error;
    } finally {
      if (sessionCreationVersion === attemptVersion) {
        sessionCreationPromise = null;
      }
    }
  })();

  return sessionCreationPromise;
};

export const ensureSession = async (): Promise<string | null> => {
  const existing = getSessionId();
  if (existing) {
    return existing;
  }

  try {
    return await createSession();
  } catch (error) {
    if (error instanceof SessionLimitError) {
      throw error;
    }
    console.error("Failed to establish session", error);
    return null;
  }
};

export const refreshSession = async (): Promise<string | null> => {
  try {
    return await createSession();
  } catch (error) {
    if (error instanceof SessionLimitError) {
      throw error;
    }
    console.error("Failed to refresh session", error);
    return null;
  }
};

export const destroySession = async (): Promise<void> => {
  invalidateSessionCreation();

  const token = await getIdToken();
  const sessionId = getSessionId();
  if (!token && !sessionId) {
    clearSession();
    clearStoredToken();
    return;
  }

  try {
    await sessionClient.delete("/auth/session", {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(sessionId ? { "X-Session-ID": sessionId } : {}),
        "X-API-Version": API_VERSION,
      },
    });
  } catch (error) {
    console.warn("Failed to destroy session", error);
  } finally {
    clearSession();
    clearStoredToken();
  }
};
